/**
 * i18n dictionary for the /shoppers marketing page.
 *
 * Detection: `?lang=fr` wins → then `Accept-Language` on the browser
 * (`navigator.language`) with any `fr*` code → else English.
 *
 * A minimal `useShoppersI18n()` hook exposes `t(key)` for string lookups
 * and `{lang, setLang}` so the toggle button can flip it manually.
 */
import { useCallback, useEffect, useState } from "react";

const STORAGE_KEY = "cyannect.shoppers.lang";

const messages = {
  en: {
    for_shoppers: "For Shoppers",
    hero_h1_a: "Shop. Save.",
    hero_h1_b: "Get Rewarded.",
    hero_p1: "Discover offers from participating brands and businesses, find the deal that's right for you, and unlock savings with the Cyannect App.",
    hero_p2: "Browse merchant storefronts, explore available coupons and offers, and earn Cyannect Rewards on eligible purchases — all from one app.",
    hero_note: "The Cyannect website is designed for businesses and merchants. Shopper offers and coupon activation are available through the Cyannect mobile app.",
    scan_download: "Scan to Download Cyannect",

    discover_tag: "Discover More Ways to Save",
    discover_h2: "Your Favourite Stores. Their Offers. One App.",
    discover_p1: "Finding a great offer shouldn't mean searching through endless coupon websites or trying codes that may no longer work.",
    discover_p2: "Cyannect brings offers from participating brands and businesses together in one convenient shopping app. Each participating merchant has its own Cyannect Storefront, where you can explore available offers and choose the one that works for you.",

    flow_discover: "Discover",
    flow_choose: "Choose",
    flow_activate: "Activate",
    flow_shop: "Shop",
    flow_save: "Save",

    storefronts_tag: "Explore Brand Storefronts",
    storefronts_h2: "One Brand. Multiple Offers.",
    storefronts_p1: "Instead of showing you dozens of disconnected coupons from the same business, Cyannect organizes offers by merchant. You might see:",
    active_offers: "Active Offers",
    view_store: "View Store",
    storefronts_cta_note: "Open the merchant's Cyannect Storefront to explore its available offers.",
    example_for: "For example:",
    ex_running_shoes: "Running Shoes",
    ex_jackets: "Selected Jackets",
    ex_backpacks: "Selected Backpacks",
    ex_basket: "$150+ Qualifying Orders",
    ex_rewards_note: "Some offers may also include Cyannect Rewards. Offers shown above are examples for illustration. Actual brands, discounts and availability depend on participating merchants.",

    best_tag: "Find the Best Available Offers",
    best_h2: "See the Savings Before You Open the Store.",
    best_p1: "When a participating merchant has several offers available, Cyannect can highlight its highest available discount on the storefront card. For example:",
    up_to_25: "Up to 25% OFF",
    best_p2: "This helps you quickly discover stores with attractive offers. Open the storefront to see which products, categories or purchases qualify.",
    best_note: "\"Up to\" discounts may apply only to selected products, categories or qualifying purchases. Always review the individual offer terms.",

    how_tag: "How Cyannect Works",
    how_h2: "Saving With Cyannect Is Simple.",
    how_step1_t: "Download the Cyannect App",
    how_step1_b: "Scan the QR code or download Cyannect from your device's app store.",
    how_step2_t: "Discover Participating Stores",
    how_step2_b: "Open Cyannect and browse participating brands and businesses. Discover stores and offers based on availability and applicable factors such as your selected location.",
    how_step3_t: "Open a Storefront",
    how_step3_b: "See an interesting offer? Open the merchant's Cyannect Storefront. Instead of seeing only one coupon, you can browse the different offers currently available from that merchant.",
    how_step4_t: "Choose Your Offer",
    how_step4_b: "Select the product, category or store offer that works for you. Review discount, eligible products, minimum purchase where applicable, expiration, location eligibility, redemption instructions, Cyannect Rewards where applicable, and offer terms.",
    how_step5_t: "Activate Your Offer",
    how_step5_b: 'Tap "Get Offer" or "Reveal Offer". Cyannect activates the offer for you. Some offers may have a limited redemption period after activation, so make sure to check the displayed terms and expiry.',
    how_step6_t: "Shop With the Merchant",
    how_step6_b: "Tap Shop Now to continue to the participating merchant's website or app, or follow the applicable instructions for an eligible in-store offer. Complete your qualifying purchase according to the offer terms.",
    how_step7_t: "Save & Earn",
    how_step7_b: "Receive the applicable discount and, where offered, earn Cyannect Rewards on your eligible purchase.",

    mtc_tag: "More Than Coupons",
    mtc_h2: "Save Now. Earn for Later.",
    mtc_p1: "Cyannect is designed to give shoppers more than just access to discounts. With eligible offers, you can:",
    mtc_save_t: "Save Instantly",
    mtc_save_b: "Activate participating merchant offers and receive applicable discounts on qualifying purchases.",
    mtc_earn_t: "Earn Cyannect Rewards",
    mtc_earn_b: "Eligible purchases may earn rewards that accumulate in your Cyannect account.",
    mtc_redeem_t: "Redeem Your Rewards",
    mtc_redeem_b: "Once your Available Rewards reach the applicable minimum redemption amount, you can use eligible rewards toward digital gift cards available in the Cyannect Rewards catalogue.",

    rewards_tag: "Cyannect Rewards",
    rewards_h2: "Your Shopping Can Be More Rewarding.",
    rewards_p1: "When you make an eligible purchase through a qualifying Cyannect offer, rewards may be added to your account. Your app keeps track of:",
    pending_t: "Pending Rewards",
    pending_b: "Rewards associated with eligible purchases that are still being verified.",
    available_t: "Available Rewards",
    available_b: "Approved rewards that are available for eligible redemption.",
    lifetime_t: "Lifetime Rewards",
    lifetime_b: "See how much you've earned through Cyannect over time.",

    gc_tag: "Redeem Rewards for Gift Cards",
    gc_h2: "Turn Your Rewards Into Something You'll Enjoy.",
    gc_step1: "Once you have reached the required Available Rewards balance, open the Rewards section of the Cyannect App.",
    gc_step2: "Browse eligible digital gift cards available in the Rewards catalogue.",
    gc_step3: "Choose your preferred gift card and denomination.",
    gc_step4: "Confirm your redemption. Your digital gift card will then be made available according to the applicable fulfillment process.",
    gc_earn: "Earn",
    gc_accumulate: "Accumulate",
    gc_redeem: "Redeem",
    gc_enjoy: "Enjoy",
    gc_note: "Gift-card brands, denominations and availability may vary by country, provider and time.",

    offer_types_tag: "Shop Your Way",
    offer_types_h2: "Different Stores. Different Offers.",
    offer_types_p: "Participating merchants can provide several types of offers through Cyannect.",
    ot_product_t: "Product Offers", ot_product_ex: "20% OFF Selected Running Shoes", ot_product_b: "Save on a particular product.",
    ot_category_t: "Category Offers", ot_category_ex: "15% OFF Jackets", ot_category_b: "Discover savings across eligible products in a category.",
    ot_storewide_t: "Storewide Offers", ot_storewide_ex: "10% OFF Eligible Store Purchases", ot_storewide_b: "Save across qualifying purchases from a participating merchant.",
    ot_basket_t: "Basket Offers", ot_basket_ex: "$25 OFF $200+", ot_basket_b: "Unlock savings after meeting an eligible purchase threshold.",
    ot_rewards_t: "Rewards Offers", ot_rewards_ex: "Earn Cyannect Rewards on eligible purchase", ot_rewards_b: "Earn Cyannect Rewards on qualifying purchases.",
    ot_combined_t: "Combined Offers", ot_combined_ex: "Discount + Cyannect Rewards", ot_combined_b: "Where available, an offer may combine an eligible merchant discount with Cyannect Rewards.",
    example_prefix: "Example:",

    ol_tag: "Discover Online & Local Offers",
    ol_h2: "Offers That Fit Where and How You Shop.",
    ol_p: "Cyannect can support participating:",
    ol_online: "Online stores", ol_retail: "Retail businesses", ol_restaurants: "Restaurants",
    ol_local: "Local merchants", ol_services: "Service businesses", ol_ecom: "E-commerce brands",
    ol_multi: "Multi-location businesses",
    ol_note: "Offer availability can vary based on merchant participation, location, dates, products and eligibility requirements.",

    why_tag: "Why Shoppers Use Cyannect",
    why_h2: "Real Reasons Shoppers Stick With Cyannect.",
    why_one_t: "One App for Discovering Savings", why_one_b: "Explore offers from participating merchants without jumping between multiple coupon sources.",
    why_multi_t: "Multiple Offers From the Same Store", why_multi_b: "Open a merchant storefront and browse its available offers in one place.",
    why_easy_t: "Easy Offer Activation", why_easy_b: "Choose the offer you want and activate it directly through Cyannect.",
    why_relevant_t: "Relevant Offers", why_relevant_b: "Discover participating merchants and offers appropriate to applicable location and eligibility settings.",
    why_rewards_t: "Rewards on Eligible Purchases", why_rewards_b: "Some qualifying purchases can earn Cyannect Rewards in addition to the applicable merchant savings.",
    why_gc_t: "Gift Card Redemption", why_gc_b: "Accumulate eligible Available Rewards and redeem them for available digital gift cards once you meet the applicable redemption requirements.",

    app_tag: "Shoppers Use the App",
    app_h2: "Ready to Discover Your Next Offer?",
    app_p1: "Cyannect.com is primarily the business platform used by participating brands, merchants and business partners. The shopping experience lives in the Cyannect App.",
    app_p2: "Download the app to:",
    app_check_1: "Discover participating brands",
    app_check_2: "Browse merchant storefronts",
    app_check_3: "Find available offers",
    app_check_4: "Activate coupons",
    app_check_5: "Shop with participating merchants",
    app_check_6: "Track eligible rewards",
    app_check_7: "Redeem eligible rewards for available digital gift cards",

    download_tag: "Download Cyannect",
    download_h2: "Start Discovering Offers Today.",
    download_scan: "Scan with your phone to download Cyannect.",
    download_note: "Available for iOS and Android.",

    final_tag: "Final CTA",
    final_h2_a: "Better Offers. Smarter Shopping.",
    final_h2_b: "More Rewards.",
    final_p: "Discover participating merchants and their latest offers with Cyannect. Your next saving starts in the app.",

    biz_tag: "Business Cross-Link",
    biz_h2: "Are You a Brand or Business?",
    biz_p: "Create your Cyannect Storefront, publish offers and connect with shoppers looking for ways to save.",
    biz_cta: "Cyannect for Business",
    biz_cta_signup: "Merchant Sign Up",

    faq_tag: "Shopper FAQ",
    faq_h2: "Answers Before You Even Ask.",

    sticky_title: "Find Offers in the Cyannect App",
    sticky_sub: "Available for iOS and Android",
    sticky_download: "Download App",
    sticky_open: "Open Cyannect",

    lang_toggle_en: "EN",
    lang_toggle_fr: "FR",
  },
  fr: {
    for_shoppers: "Pour les acheteurs",
    hero_h1_a: "Magasinez. Économisez.",
    hero_h1_b: "Soyez récompensé.",
    hero_p1: "Découvrez les offres des marques et commerces participants, trouvez la promotion qui vous convient et débloquez des économies grâce à l'application Cyannect.",
    hero_p2: "Parcourez les vitrines des commerçants, explorez les coupons et offres disponibles, et gagnez des récompenses Cyannect sur vos achats admissibles — le tout dans une seule application.",
    hero_note: "Le site Cyannect est conçu pour les entreprises et commerçants. Les offres pour acheteurs et l'activation des coupons se font dans l'application mobile Cyannect.",
    scan_download: "Numérisez pour télécharger Cyannect",

    discover_tag: "Découvrez plus de façons d'économiser",
    discover_h2: "Vos magasins préférés. Leurs offres. Une seule appli.",
    discover_p1: "Trouver une bonne offre ne devrait pas signifier parcourir d'innombrables sites de coupons ou essayer des codes qui ne fonctionnent plus.",
    discover_p2: "Cyannect rassemble les offres des marques et commerces participants dans une seule application. Chaque commerçant participant possède sa propre vitrine Cyannect où vous pouvez explorer ses offres et choisir celle qui vous convient.",

    flow_discover: "Découvrir",
    flow_choose: "Choisir",
    flow_activate: "Activer",
    flow_shop: "Acheter",
    flow_save: "Économiser",

    storefronts_tag: "Explorez les vitrines de marques",
    storefronts_h2: "Une marque. Plusieurs offres.",
    storefronts_p1: "Au lieu de vous montrer des dizaines de coupons dispersés provenant du même commerce, Cyannect organise les offres par commerçant. Vous pourriez voir :",
    active_offers: "offres actives",
    view_store: "Voir le magasin",
    storefronts_cta_note: "Ouvrez la vitrine Cyannect du commerçant pour explorer ses offres disponibles.",
    example_for: "Par exemple :",
    ex_running_shoes: "Chaussures de course",
    ex_jackets: "Vestes sélectionnées",
    ex_backpacks: "Sacs à dos sélectionnés",
    ex_basket: "Commandes de 150 $ et plus",
    ex_rewards_note: "Certaines offres peuvent aussi inclure des récompenses Cyannect. Les offres présentées sont des exemples à titre indicatif. Les marques, les remises et la disponibilité réelles dépendent des commerçants participants.",

    best_tag: "Trouvez les meilleures offres disponibles",
    best_h2: "Voyez les économies avant d'ouvrir le magasin.",
    best_p1: "Lorsqu'un commerçant participant propose plusieurs offres, Cyannect peut mettre en évidence sa plus grande remise disponible sur la carte de vitrine. Par exemple :",
    up_to_25: "Jusqu'à 25 % de rabais",
    best_p2: "Cela vous aide à découvrir rapidement les magasins avec des offres attrayantes. Ouvrez la vitrine pour voir quels produits, catégories ou achats sont admissibles.",
    best_note: "Les remises « jusqu'à » peuvent ne s'appliquer qu'à certains produits, catégories ou achats admissibles. Consultez toujours les conditions individuelles de l'offre.",

    how_tag: "Comment fonctionne Cyannect",
    how_h2: "Économiser avec Cyannect, c'est simple.",
    how_step1_t: "Téléchargez l'application Cyannect",
    how_step1_b: "Numérisez le code QR ou téléchargez Cyannect depuis la boutique d'applications de votre appareil.",
    how_step2_t: "Découvrez les magasins participants",
    how_step2_b: "Ouvrez Cyannect et parcourez les marques et commerces participants. Découvrez les magasins et les offres en fonction de la disponibilité et de facteurs applicables comme votre emplacement sélectionné.",
    how_step3_t: "Ouvrez une vitrine",
    how_step3_b: "Une offre vous intéresse ? Ouvrez la vitrine Cyannect du commerçant. Au lieu de voir un seul coupon, parcourez les différentes offres actuellement disponibles chez ce commerçant.",
    how_step4_t: "Choisissez votre offre",
    how_step4_b: "Sélectionnez le produit, la catégorie ou l'offre du magasin qui vous convient. Vérifiez la remise, les produits admissibles, l'achat minimum le cas échéant, l'expiration, l'admissibilité géographique, les instructions de rachat, les récompenses Cyannect le cas échéant, et les conditions de l'offre.",
    how_step5_t: "Activez votre offre",
    how_step5_b: "Touchez « Obtenir l'offre » ou « Révéler l'offre ». Cyannect active l'offre pour vous. Certaines offres ont une période de rachat limitée après activation ; vérifiez les conditions et la date d'expiration affichées.",
    how_step6_t: "Achetez auprès du commerçant",
    how_step6_b: "Touchez « Acheter maintenant » pour continuer vers le site ou l'application du commerçant participant, ou suivez les instructions applicables pour une offre en magasin admissible. Complétez votre achat selon les conditions de l'offre.",
    how_step7_t: "Économisez et gagnez",
    how_step7_b: "Bénéficiez de la remise applicable et, le cas échéant, gagnez des récompenses Cyannect sur votre achat admissible.",

    mtc_tag: "Plus que des coupons",
    mtc_h2: "Économisez maintenant. Gagnez pour plus tard.",
    mtc_p1: "Cyannect vise à offrir aux acheteurs bien plus que de simples remises. Avec les offres admissibles, vous pouvez :",
    mtc_save_t: "Économiser instantanément",
    mtc_save_b: "Activez les offres des commerçants participants et recevez les remises applicables sur vos achats admissibles.",
    mtc_earn_t: "Gagner des récompenses Cyannect",
    mtc_earn_b: "Les achats admissibles peuvent générer des récompenses qui s'accumulent dans votre compte Cyannect.",
    mtc_redeem_t: "Échanger vos récompenses",
    mtc_redeem_b: "Une fois que vos récompenses disponibles atteignent le montant minimum de rachat applicable, vous pouvez les utiliser pour obtenir des cartes-cadeaux numériques du catalogue Cyannect.",

    rewards_tag: "Récompenses Cyannect",
    rewards_h2: "Vos achats peuvent être plus gratifiants.",
    rewards_p1: "Lorsque vous effectuez un achat admissible via une offre Cyannect admissible, des récompenses peuvent être ajoutées à votre compte. Votre application suit :",
    pending_t: "Récompenses en attente",
    pending_b: "Récompenses associées à des achats admissibles encore en cours de vérification.",
    available_t: "Récompenses disponibles",
    available_b: "Récompenses approuvées qui peuvent être échangées.",
    lifetime_t: "Récompenses à vie",
    lifetime_b: "Voyez combien vous avez gagné avec Cyannect au fil du temps.",

    gc_tag: "Échangez vos récompenses contre des cartes-cadeaux",
    gc_h2: "Transformez vos récompenses en quelque chose qui vous plaît.",
    gc_step1: "Une fois le solde requis de récompenses disponibles atteint, ouvrez la section Récompenses de l'application Cyannect.",
    gc_step2: "Parcourez les cartes-cadeaux numériques admissibles disponibles dans le catalogue.",
    gc_step3: "Choisissez votre carte-cadeau et sa dénomination.",
    gc_step4: "Confirmez votre échange. Votre carte-cadeau numérique sera ensuite mise à disposition selon le processus applicable.",
    gc_earn: "Gagner",
    gc_accumulate: "Accumuler",
    gc_redeem: "Échanger",
    gc_enjoy: "Profiter",
    gc_note: "Les marques, dénominations et disponibilités des cartes-cadeaux peuvent varier selon le pays, le fournisseur et le moment.",

    offer_types_tag: "Achetez à votre façon",
    offer_types_h2: "Des magasins différents. Des offres différentes.",
    offer_types_p: "Les commerçants participants peuvent proposer plusieurs types d'offres via Cyannect.",
    ot_product_t: "Offres produit", ot_product_ex: "20 % de rabais sur des chaussures de course sélectionnées", ot_product_b: "Économisez sur un produit particulier.",
    ot_category_t: "Offres par catégorie", ot_category_ex: "15 % de rabais sur les vestes", ot_category_b: "Découvrez des économies sur les produits admissibles d'une catégorie.",
    ot_storewide_t: "Offres à l'échelle du magasin", ot_storewide_ex: "10 % de rabais sur les achats admissibles", ot_storewide_b: "Économisez sur les achats admissibles chez un commerçant participant.",
    ot_basket_t: "Offres par panier", ot_basket_ex: "25 $ de rabais à partir de 200 $", ot_basket_b: "Débloquez des économies après avoir atteint un seuil d'achat admissible.",
    ot_rewards_t: "Offres avec récompenses", ot_rewards_ex: "Gagnez des récompenses Cyannect sur un achat admissible", ot_rewards_b: "Gagnez des récompenses Cyannect sur les achats admissibles.",
    ot_combined_t: "Offres combinées", ot_combined_ex: "Remise + récompenses Cyannect", ot_combined_b: "Le cas échéant, une offre peut combiner une remise admissible avec des récompenses Cyannect.",
    example_prefix: "Exemple :",

    ol_tag: "Découvrez les offres en ligne et locales",
    ol_h2: "Des offres adaptées à où et comment vous magasinez.",
    ol_p: "Cyannect peut prendre en charge les :",
    ol_online: "Magasins en ligne", ol_retail: "Commerces de détail", ol_restaurants: "Restaurants",
    ol_local: "Commerçants locaux", ol_services: "Entreprises de services", ol_ecom: "Marques e-commerce",
    ol_multi: "Entreprises multi-emplacements",
    ol_note: "La disponibilité des offres peut varier selon la participation du commerçant, l'emplacement, les dates, les produits et les critères d'admissibilité.",

    why_tag: "Pourquoi les acheteurs utilisent Cyannect",
    why_h2: "De vraies raisons de rester avec Cyannect.",
    why_one_t: "Une seule application pour découvrir vos économies", why_one_b: "Explorez les offres des commerçants participants sans passer d'une source de coupons à une autre.",
    why_multi_t: "Plusieurs offres du même magasin", why_multi_b: "Ouvrez la vitrine d'un commerçant et parcourez ses offres disponibles en un seul endroit.",
    why_easy_t: "Activation facile des offres", why_easy_b: "Choisissez l'offre souhaitée et activez-la directement via Cyannect.",
    why_relevant_t: "Offres pertinentes", why_relevant_b: "Découvrez des commerçants et des offres adaptés à votre emplacement et à vos paramètres d'admissibilité.",
    why_rewards_t: "Récompenses sur achats admissibles", why_rewards_b: "Certains achats admissibles peuvent générer des récompenses Cyannect en plus des économies applicables.",
    why_gc_t: "Rachat en cartes-cadeaux", why_gc_b: "Accumulez vos récompenses disponibles admissibles et échangez-les contre des cartes-cadeaux numériques disponibles.",

    app_tag: "Les acheteurs utilisent l'appli",
    app_h2: "Prêt à découvrir votre prochaine offre ?",
    app_p1: "Cyannect.com est principalement la plateforme d'affaires utilisée par les marques et commerçants participants. L'expérience d'achat se trouve dans l'application Cyannect.",
    app_p2: "Téléchargez l'application pour :",
    app_check_1: "Découvrir les marques participantes",
    app_check_2: "Parcourir les vitrines des commerçants",
    app_check_3: "Trouver les offres disponibles",
    app_check_4: "Activer des coupons",
    app_check_5: "Acheter chez les commerçants participants",
    app_check_6: "Suivre vos récompenses admissibles",
    app_check_7: "Échanger vos récompenses contre des cartes-cadeaux numériques",

    download_tag: "Télécharger Cyannect",
    download_h2: "Commencez à découvrir des offres aujourd'hui.",
    download_scan: "Numérisez avec votre téléphone pour télécharger Cyannect.",
    download_note: "Disponible sur iOS et Android.",

    final_tag: "Appel final",
    final_h2_a: "Meilleures offres. Achats plus intelligents.",
    final_h2_b: "Plus de récompenses.",
    final_p: "Découvrez des commerçants participants et leurs dernières offres avec Cyannect. Vos prochaines économies commencent dans l'application.",

    biz_tag: "Lien affaires",
    biz_h2: "Vous êtes une marque ou une entreprise ?",
    biz_p: "Créez votre vitrine Cyannect, publiez vos offres et connectez-vous avec des acheteurs qui recherchent des économies.",
    biz_cta: "Cyannect pour les entreprises",
    biz_cta_signup: "Inscription commerçant",

    faq_tag: "FAQ acheteurs",
    faq_h2: "Les réponses avant même vos questions.",

    sticky_title: "Trouvez des offres dans l'appli Cyannect",
    sticky_sub: "Disponible sur iOS et Android",
    sticky_download: "Télécharger",
    sticky_open: "Ouvrir Cyannect",

    lang_toggle_en: "EN",
    lang_toggle_fr: "FR",
  },
};

const FAQ_EN = [
  { q: "Can I use Cyannect coupons directly on this website?", a: "Shopper coupon discovery and activation are provided through the Cyannect mobile app. Download the app to access available shopper offers." },
  { q: "Why do I need the Cyannect App?", a: "The app provides the consumer experience for discovering merchant storefronts, browsing offers, activating coupons, tracking eligible rewards and accessing reward redemptions." },
  { q: "Can a store have more than one offer?", a: "Yes. Participating merchants may publish multiple offers inside their Cyannect Storefront. Open the storefront to see the offers currently available to you." },
  { q: 'What does "Up to 25% OFF" mean?', a: "It means an available offer from that merchant may provide savings of up to the displayed percentage. The highest discount may apply only to selected products, categories or qualifying purchases." },
  { q: "How do I activate an offer?", a: "Select an offer in the Cyannect App and tap Get Offer or Reveal Offer. Follow the displayed instructions and applicable terms." },
  { q: "Can offers expire?", a: "Yes. Offers may have fixed expiration dates or, where applicable, a limited redemption period after activation. Check the individual offer for details." },
  { q: "What are Cyannect Rewards?", a: "Cyannect Rewards may be earned on eligible purchases associated with qualifying offers. Rewards are subject to verification and applicable program terms." },
  { q: "When can I use my rewards?", a: "Once your Available Rewards reach the applicable minimum redemption requirement, eligible rewards may be redeemed for available digital gift cards through the Cyannect App." },
  { q: "Where can I see my rewards?", a: "Open the Rewards section of your Cyannect App to see Pending Rewards, Available Rewards, reward activity and eligible redemptions." },
  { q: "Are all offers available everywhere?", a: "No. Offer availability may depend on the merchant, product, location, campaign dates and other eligibility requirements." },
];

const FAQ_FR = [
  { q: "Puis-je utiliser les coupons Cyannect directement sur ce site ?", a: "La découverte et l'activation des coupons pour les acheteurs se font dans l'application mobile Cyannect. Téléchargez l'application pour accéder aux offres disponibles." },
  { q: "Pourquoi ai-je besoin de l'application Cyannect ?", a: "L'application offre l'expérience client complète : découverte des vitrines, exploration des offres, activation des coupons, suivi des récompenses et échange contre des cartes-cadeaux." },
  { q: "Un magasin peut-il proposer plusieurs offres ?", a: "Oui. Les commerçants participants peuvent publier plusieurs offres dans leur vitrine Cyannect. Ouvrez la vitrine pour voir les offres actuellement disponibles." },
  { q: "Que signifie « Jusqu'à 25 % de rabais » ?", a: "Cela signifie qu'une offre disponible chez ce commerçant peut offrir jusqu'au pourcentage affiché. La remise maximale peut ne s'appliquer qu'à certains produits, catégories ou achats admissibles." },
  { q: "Comment activer une offre ?", a: "Sélectionnez une offre dans l'application Cyannect et touchez « Obtenir l'offre » ou « Révéler l'offre ». Suivez les instructions affichées et les conditions applicables." },
  { q: "Les offres peuvent-elles expirer ?", a: "Oui. Les offres peuvent avoir une date d'expiration fixe ou, le cas échéant, une période de rachat limitée après activation. Consultez les détails de chaque offre." },
  { q: "Que sont les récompenses Cyannect ?", a: "Les récompenses Cyannect peuvent être gagnées sur des achats admissibles associés à des offres admissibles. Les récompenses sont sujettes à vérification et aux conditions du programme applicables." },
  { q: "Quand puis-je utiliser mes récompenses ?", a: "Une fois vos récompenses disponibles à hauteur du minimum requis, elles peuvent être échangées contre des cartes-cadeaux numériques via l'application Cyannect." },
  { q: "Où puis-je voir mes récompenses ?", a: "Ouvrez la section Récompenses de l'application Cyannect pour voir les récompenses en attente, disponibles, votre activité et les échanges admissibles." },
  { q: "Toutes les offres sont-elles disponibles partout ?", a: "Non. La disponibilité peut dépendre du commerçant, du produit, de l'emplacement, des dates de campagne et d'autres critères d'admissibilité." },
];

function detectLang() {
  try {
    const url = new URLSearchParams(window.location.search).get("lang");
    if (url === "fr" || url === "en") return url;
    const stored = localStorage.getItem(STORAGE_KEY);
    if (stored === "fr" || stored === "en") return stored;
    const nav = (navigator.language || "").toLowerCase();
    if (nav.startsWith("fr")) return "fr";
    const langs = (navigator.languages || []).map((l) => l.toLowerCase());
    if (langs.some((l) => l.startsWith("fr"))) return "fr";
  } catch {}
  return "en";
}

export function useShoppersI18n() {
  const [lang, setLangState] = useState("en");
  useEffect(() => { setLangState(detectLang()); }, []);
  const setLang = useCallback((next) => {
    setLangState(next);
    try { localStorage.setItem(STORAGE_KEY, next); } catch {}
  }, []);
  const t = useCallback((key) => {
    return (messages[lang] && messages[lang][key]) || messages.en[key] || key;
  }, [lang]);
  const faq = lang === "fr" ? FAQ_FR : FAQ_EN;
  return { lang, setLang, t, faq };
}
